import { Button, Form, Input, Select, Spin } from "antd"
import { graphql } from "gatsby"
import React, { useEffect, useState } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import Layout from "../components/Layout"
import News from "../components/News"
import PageHero from "../components/PageHero"
import { emails } from "../data"
import { postFormData } from "../services/contact"
import "../styles/contact.scss"
const { TextArea } = Input
const { Option } = Select

const url = "https://staging.visiomate.com/crmPipeline"

const Contact = ({ data, career }) => {
  const [loading, setLoading] = useState(false)
  const [form] = Form.useForm()
  const [fileBase64, setFileBase64] = useState("")
  const [fileName, setFileName] = useState("")
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [isVerified, setIsVerified] = useState(false)

  useEffect(() => {
    const getToken = async () => {
      if (!executeRecaptcha) return
      const token = await executeRecaptcha("contact_form")
      const res = await fetch("https://staging.visiomate.com/verify-recaptcha-site", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      })
      const data = await res.json()
      setIsVerified(data.success && data.score > 0.5)
    }
    getToken()
  }, [executeRecaptcha])

  const handleFileChange = e => {
    const file = e.target.files[0]
    if (!file) return
    setFileName(file.name)
    const reader = new FileReader()
    reader.onload = () => setFileBase64(reader.result.split(",")[1] || "")
    reader.readAsDataURL(file)
  }

  const onFinishForFirstForm = values => {
    handleFormData(values)
    form.resetFields()
    setFileName("")
    setFileBase64("")
  }

  const handleFormData = async values => {
    const { email, phone, message, firstName, lastName, country, company, role, headcount, revenue, projectStage, budget, timeline } = values
    const description = [
      `Company: ${company || "N/A"}`,
      `Role: ${role || "N/A"}`,
      `Country: ${country || "N/A"}`,
      `Current Headcount: ${headcount || "N/A"}`,
      `Annual Revenue: ${revenue || "N/A"}`,
      `Project Stage: ${projectStage || "N/A"}`,
      `Estimated Budget: ${budget || "N/A"}`,
      `Ideal Timeline: ${timeline || "N/A"}`,
      `\nMessage:\n${message || ""}`,
    ].join("\n")
    const formData = {
      name: "Contact Us: " + (company || firstName + " " + lastName),
      contact_name: firstName + " " + lastName,
      email_from: email,
      phone: phone,
      message: message,
      description,
      email_to: emails.info,
      attachment_base64: fileBase64,
    }
    setLoading(true)
    await postFormData(formData, setLoading, url)
    setLoading(false)
  }

  return (
    <Layout className="contact-us-container" data={data}
      pageTitle={"Visiomate - Contact us"}
      descrip={"Have questions or need assistance? Contact us today! Our friendly team is ready to help. Find our contact information, including phone, email, and address, here."}>
      <PageHero eyebrow="Contact Us" title="We're excited to connect with you!" description="Whether you're seeking information about our services, exploring collaboration prospects, or just dropping by to say hello — our team is here and eager to support you." />
      <div className="message">
        <p>
          Your thoughts and inquiries are important to us. We are here to assist
          you in any way possible, so please take a moment to share how we can
          be of service
        </p>
      </div>
      <div className="position-relative">
        <Form form={form} onFinish={onFinishForFirstForm} layout="vertical">
          <div className="form-container">
            <div className="contact-form-grid">

              {/* Row 1 */}
              <Form.Item label="First Name" name="firstName" rules={[{ required: true, message: "Please enter your first name" }]}>
                <Input placeholder="First Name" />
              </Form.Item>
              <Form.Item label="Last Name" name="lastName" rules={[{ required: true, message: "Please enter your last name" }]}>
                <Input placeholder="Last Name" />
              </Form.Item>

              {/* Row 2 */}
              <Form.Item label="Country" name="country" rules={[{ required: true, message: "Please select your country" }]}>
                <Select placeholder="Select Country" showSearch optionFilterProp="children">
                  <Option value="Pakistan">Pakistan</Option>
                  <Option value="United States">United States</Option>
                  <Option value="United Kingdom">United Kingdom</Option>
                  <Option value="Canada">Canada</Option>
                  <Option value="Australia">Australia</Option>
                  <Option value="United Arab Emirates">United Arab Emirates</Option>
                  <Option value="Saudi Arabia">Saudi Arabia</Option>
                  <Option value="Qatar">Qatar</Option>
                  <Option value="Kuwait">Kuwait</Option>
                  <Option value="Bahrain">Bahrain</Option>
                  <Option value="Oman">Oman</Option>
                  <Option value="India">India</Option>
                  <Option value="Germany">Germany</Option>
                  <Option value="France">France</Option>
                  <Option value="Netherlands">Netherlands</Option>
                  <Option value="Singapore">Singapore</Option>
                  <Option value="Other">Other</Option>
                </Select>
              </Form.Item>
              <Form.Item label="Company/Organisation Name" name="company" rules={[{ required: true, message: "Please enter your company name" }]}>
                <Input placeholder="Company/Organisation Name" />
              </Form.Item>

              {/* Row 3 */}
              <Form.Item label="Your Role in the Company" name="role" rules={[{ required: true, message: "Please enter your role" }]}>
                <Input placeholder="Your Role in the Company" />
              </Form.Item>
              <Form.Item label="Business Email" name="email" rules={[{ required: true, message: "Please enter your email" }, { type: "email", message: "Please enter a valid email address" }]}>
                <Input placeholder="Business Email" />
              </Form.Item>

              {/* Row 4 */}
              <Form.Item label="Phone" name="phone" rules={[{ required: false }]}>
                <Input placeholder="+1 234 567 8900" />
              </Form.Item>
              <Form.Item label="Current Headcount" name="headcount" rules={[{ required: true, message: "Please select your headcount" }]}>
                <Select placeholder="Select Headcount">
                  <Option value="1–10">1–10</Option>
                  <Option value="11–50">11–50</Option>
                  <Option value="51–200">51–200</Option>
                  <Option value="201–500">201–500</Option>
                  <Option value="500+">500+</Option>
                </Select>
              </Form.Item>

              {/* Row 5 */}
              <Form.Item label="Your Annual Revenue" name="revenue" rules={[{ required: true, message: "Please select your annual revenue" }]}>
                <Select placeholder="Select Annual Revenue">
                  <Option value="Under $100K">Under $100K</Option>
                  <Option value="$100K–$500K">$100K–$500K</Option>
                  <Option value="$500K–$1M">$500K–$1M</Option>
                  <Option value="$1M–$5M">$1M–$5M</Option>
                  <Option value="$5M–$10M">$5M–$10M</Option>
                  <Option value="$10M+">$10M+</Option>
                </Select>
              </Form.Item>
              <Form.Item label="Project Stage" name="projectStage" rules={[{ required: true, message: "Please select your project stage" }]}>
                <Select placeholder="Select Project Stage">
                  <Option value="Idea / Concept">Idea / Concept</Option>
                  <Option value="Planning">Planning</Option>
                  <Option value="In Development">In Development</Option>
                  <Option value="Launched">Launched</Option>
                  <Option value="Scaling">Scaling</Option>
                </Select>
              </Form.Item>

              {/* Row 6 */}
              <Form.Item label="What is your estimated budget for this project?" name="budget" rules={[{ required: true, message: "Please select your budget" }]}>
                <Select placeholder="Select Budget Range">
                  <Option value="Under $5K">Under $5K</Option>
                  <Option value="$5K–$15K">$5K–$15K</Option>
                  <Option value="$15K–$50K">$15K–$50K</Option>
                  <Option value="$50K–$100K">$50K–$100K</Option>
                  <Option value="$100K+">$100K+</Option>
                </Select>
              </Form.Item>
              <Form.Item label="What is your ideal timeline for launching this software?" name="timeline" rules={[{ required: true, message: "Please select your timeline" }]}>
                <Select placeholder="Select Timeline">
                  <Option value="ASAP">ASAP</Option>
                  <Option value="1–3 months">1–3 months</Option>
                  <Option value="3–6 months">3–6 months</Option>
                  <Option value="6–12 months">6–12 months</Option>
                  <Option value="12+ months">12+ months</Option>
                </Select>
              </Form.Item>

              {/* Row 7 — full width */}
              <Form.Item label="Tell us a bit about what you're looking to solve" name="message" rules={[{ required: true, message: "Please tell us what you're looking to solve" }]} className="contact-full-width">
                <TextArea placeholder="Describe your challenge or project..." autoSize={{ minRows: 4, maxRows: 8 }} />
              </Form.Item>

              {/* Row 8 — file upload full width */}
              <div className="contact-full-width contact-file-upload">
                <p className="contact-file-label-text">Attach a relevant document, to help us understand your idea better.</p>
                <label htmlFor="contact-file-input" className="contact-file-drop">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#1b2d8e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="16 16 12 12 8 16" /><line x1="12" y1="12" x2="12" y2="21" />
                    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                  </svg>
                  <span className="contact-file-name">{fileName || "Click to upload or drag and drop"}</span>
                  <span className="contact-file-hint">PDF, DOC/DOCX, XLS/CSV, JPG/JPEG, PNG, GIF</span>
                  <input id="contact-file-input" type="file" accept=".pdf,.doc,.docx,.xls,.csv,.jpg,.jpeg,.png,.gif" onChange={handleFileChange} style={{ display: "none" }} />
                </label>
              </div>

            </div>

            <div className="contact-submit-row">
              <Form.Item>
                <Button type="primary" htmlType="submit" className="" disabled={!isVerified || loading}>
                  {loading ? <Spin /> : "Send Message"}
                </Button>
              </Form.Item>
            </div>
          </div>
        </Form>
        {/* <IconsAdded iconColor={"yellow"}/> */}
      </div>
      <div></div>
      <div className="main-div">
        <h1 style={{ marginBottom: "1rem" }}>Feel free to connect, we'd love to hear from you!</h1>
        <div className="map-and-form-container">
          <div className="">
            <iframe
              title="Pakistan office location"
              className="image-map"
              style={{ width: "100%", border: "none" }}
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3404.2537336474825!2d74.35323797621331!3d31.434680951369614!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391903fc936aaaab%3A0x3a4403a7667c4e04!2sVisiomate!5e0!3m2!1sen!2s!4v1706611182963!5m2!1sen!2s"
            ></iframe>
          </div>
          <div className="VisiomateLoaction">
            <p className="contact-info-eyebrow">CONTACT INFO</p>
            <p className="contact-info-subtitle">Prefer to reach out directly? Here's how to find us.</p>
            <div className="contact-info-item">
              <strong>Office</strong>
              <span>Kasur Rd, Lahore, Punjab 54700, Pakistan</span>
            </div>
            <div className="contact-info-item">
              <strong>Phone</strong>
              <span>+92 315 7798505 (Sales)</span>
              <span>+92 309 6141455 (Sales)</span>
              <span>+92 304 4797665 (Management)</span>
            </div>
            <div className="contact-info-item">
              <strong>Email</strong>
              <span>business@visiomate.com</span>
              <span>info@visiomate.com</span>
            </div>
          </div>
        </div>
      </div>

      <News isAbout={true} career={true} />
    </Layout>
  )
}

export default Contact

export const query = graphql`
  query contactQuery {
    PKFlag: file(
      relativePath: {
        eq: "contact/2634382_ensign_flag_nation_pakistan_icon.png"
      }
    ) {
      childImageSharp {
        fluid(quality: 100, maxWidth: 1000) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    usFlag: file(relativePath: { eq: "contact/7849795_1.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    VMLogoWhite: file(relativePath: { eq: "logo_white_LST.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
    contactbg: file(relativePath: { eq: "contact_bg.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
